/**
 * Timbrica 3D Printing — shared client-side module.
 * Exposes common helpers for the 3d-* tool suite via window.Timbrica3D.
 *
 * Depends on three.js r147 (UMD, window.THREE) being loaded BEFORE this script
 * when the tool uses 3D rendering. Pure utilities below (density presets,
 * STL volume math, STL writer, downloadBlob, telemetry) work without THREE.
 */
(function (global) {
    'use strict';

    // ─── Density table (g/cm³) — source: manufacturer datasheets 2024–2025.
    // Keep in sync with translation strings in tools/3d-filament-converter.php
    var FILAMENT_PRESETS = [
        { id: 'pla',         density: 1.24 },
        { id: 'pla-silk',    density: 1.26 },
        { id: 'pla-plus',    density: 1.24 },
        { id: 'petg',        density: 1.27 },
        { id: 'abs',         density: 1.04 },
        { id: 'asa',         density: 1.07 },
        { id: 'tpu-85a',     density: 1.15 },
        { id: 'tpu-95a',     density: 1.21 },
        { id: 'nylon-pa6',   density: 1.14 },
        { id: 'nylon-pa12',  density: 1.01 },
        { id: 'pc',          density: 1.20 },
        { id: 'pc-abs',      density: 1.13 },
        { id: 'pla-carbon',  density: 1.30 },
        { id: 'petg-carbon', density: 1.29 },
        { id: 'pa-carbon',   density: 1.16 },
        { id: 'pla-wood',    density: 1.28 },
        { id: 'pla-metal',   density: 2.10 },
        { id: 'hips',        density: 1.04 },
        { id: 'pva',         density: 1.19 },
        { id: 'pp',          density: 0.90 },
        { id: 'peek',        density: 1.32 },
        { id: 'pei',         density: 1.27 },
    ];

    function densityFor(id) {
        for (var i = 0; i < FILAMENT_PRESETS.length; i++) {
            if (FILAMENT_PRESETS[i].id === id) return FILAMENT_PRESETS[i].density;
        }
        return 1.24;
    }

    // Filament volume from diameter (mm) and length (m) in cm³
    function filamentVolumeCm3(diameterMm, lengthM) {
        var r = diameterMm / 2 / 10; // cm
        return Math.PI * r * r * (lengthM * 100);
    }

    function filamentWeightG(diameterMm, lengthM, densityGcm3) {
        return filamentVolumeCm3(diameterMm, lengthM) * densityGcm3;
    }

    function filamentLengthM(weightG, diameterMm, densityGcm3) {
        var r = diameterMm / 2 / 10;
        var volCm3 = weightG / densityGcm3;
        var lengthCm = volCm3 / (Math.PI * r * r);
        return lengthCm / 100;
    }

    // ─── STL parsing — ASCII + binary, returns {positions, normals, triangles, bbox, volumeCm3}
    function parseSTL(buffer) {
        var view = new DataView(buffer);
        var isBinary = _isBinarySTL(buffer, view);
        return isBinary ? _parseBinarySTL(buffer, view) : _parseAsciiSTL(buffer);
    }

    function _isBinarySTL(buffer, view) {
        if (buffer.byteLength < 84) return false;
        var triangles = view.getUint32(80, true);
        var expectedSize = 84 + triangles * 50;
        if (expectedSize === buffer.byteLength) return true;
        // ASCII STL starts with "solid " — but some binaries do too. Fall back to size check.
        var header = new Uint8Array(buffer, 0, 5);
        var sig = String.fromCharCode(header[0], header[1], header[2], header[3], header[4]);
        return sig !== 'solid';
    }

    function _parseBinarySTL(buffer, view) {
        var triangles = view.getUint32(80, true);
        var positions = new Float32Array(triangles * 9);
        var normals = new Float32Array(triangles * 9);
        var offset = 84;
        var bbox = { min: [Infinity, Infinity, Infinity], max: [-Infinity, -Infinity, -Infinity] };
        var volume = 0;
        for (var i = 0; i < triangles; i++) {
            var nx = view.getFloat32(offset, true);
            var ny = view.getFloat32(offset + 4, true);
            var nz = view.getFloat32(offset + 8, true);
            offset += 12;
            var p = i * 9;
            for (var v = 0; v < 3; v++) {
                var x = view.getFloat32(offset, true);
                var y = view.getFloat32(offset + 4, true);
                var z = view.getFloat32(offset + 8, true);
                offset += 12;
                positions[p + v * 3]     = x;
                positions[p + v * 3 + 1] = y;
                positions[p + v * 3 + 2] = z;
                normals[p + v * 3]     = nx;
                normals[p + v * 3 + 1] = ny;
                normals[p + v * 3 + 2] = nz;
                if (x < bbox.min[0]) bbox.min[0] = x; if (x > bbox.max[0]) bbox.max[0] = x;
                if (y < bbox.min[1]) bbox.min[1] = y; if (y > bbox.max[1]) bbox.max[1] = y;
                if (z < bbox.min[2]) bbox.min[2] = z; if (z > bbox.max[2]) bbox.max[2] = z;
            }
            volume += _signedTetraVolume(
                positions[p], positions[p+1], positions[p+2],
                positions[p+3], positions[p+4], positions[p+5],
                positions[p+6], positions[p+7], positions[p+8]
            );
            offset += 2; // attribute byte count
        }
        return {
            positions: positions,
            normals: normals,
            triangles: triangles,
            bbox: bbox,
            volumeCm3: Math.abs(volume) / 1000, // mm³ → cm³
        };
    }

    function _parseAsciiSTL(buffer) {
        var text = new TextDecoder().decode(buffer);
        var positions = [];
        var normals = [];
        var bbox = { min: [Infinity, Infinity, Infinity], max: [-Infinity, -Infinity, -Infinity] };
        var volume = 0;
        var re = /facet\s+normal\s+([-0-9.eE+]+)\s+([-0-9.eE+]+)\s+([-0-9.eE+]+)[\s\S]*?outer\s+loop([\s\S]*?)endloop/g;
        var vertRe = /vertex\s+([-0-9.eE+]+)\s+([-0-9.eE+]+)\s+([-0-9.eE+]+)/g;
        var m;
        var count = 0;
        while ((m = re.exec(text)) !== null) {
            var nx = parseFloat(m[1]), ny = parseFloat(m[2]), nz = parseFloat(m[3]);
            var loop = m[4];
            vertRe.lastIndex = 0;
            var verts = [];
            var vm;
            while ((vm = vertRe.exec(loop)) !== null && verts.length < 9) {
                var x = parseFloat(vm[1]), y = parseFloat(vm[2]), z = parseFloat(vm[3]);
                verts.push(x, y, z);
                if (x < bbox.min[0]) bbox.min[0] = x; if (x > bbox.max[0]) bbox.max[0] = x;
                if (y < bbox.min[1]) bbox.min[1] = y; if (y > bbox.max[1]) bbox.max[1] = y;
                if (z < bbox.min[2]) bbox.min[2] = z; if (z > bbox.max[2]) bbox.max[2] = z;
            }
            if (verts.length !== 9) continue;
            positions.push(verts[0], verts[1], verts[2], verts[3], verts[4], verts[5], verts[6], verts[7], verts[8]);
            for (var k = 0; k < 3; k++) normals.push(nx, ny, nz);
            volume += _signedTetraVolume(
                verts[0], verts[1], verts[2],
                verts[3], verts[4], verts[5],
                verts[6], verts[7], verts[8]
            );
            count++;
        }
        return {
            positions: new Float32Array(positions),
            normals: new Float32Array(normals),
            triangles: count,
            bbox: bbox,
            volumeCm3: Math.abs(volume) / 1000,
        };
    }

    function _signedTetraVolume(x1, y1, z1, x2, y2, z2, x3, y3, z3) {
        return (x1 * (y2 * z3 - z2 * y3) - y1 * (x2 * z3 - z2 * x3) + z1 * (x2 * y3 - y2 * x3)) / 6;
    }

    // ─── Binary STL writer — expects array of triangles as Float32Array(positions) + normals.
    function writeBinarySTL(positions, normals) {
        var triangles = positions.length / 9;
        var buf = new ArrayBuffer(84 + triangles * 50);
        var view = new DataView(buf);
        // 80-byte header left zero
        view.setUint32(80, triangles, true);
        var offset = 84;
        for (var i = 0; i < triangles; i++) {
            var p = i * 9;
            view.setFloat32(offset,     normals[p],     true);
            view.setFloat32(offset + 4, normals[p + 1], true);
            view.setFloat32(offset + 8, normals[p + 2], true);
            offset += 12;
            for (var v = 0; v < 3; v++) {
                view.setFloat32(offset,     positions[p + v * 3],     true);
                view.setFloat32(offset + 4, positions[p + v * 3 + 1], true);
                view.setFloat32(offset + 8, positions[p + v * 3 + 2], true);
                offset += 12;
            }
            view.setUint16(offset, 0, true);
            offset += 2;
        }
        return new Blob([buf], { type: 'model/stl' });
    }

    // Auto-compute normals if not provided (counter-clockwise winding).
    function computeNormals(positions) {
        var n = positions.length / 9;
        var normals = new Float32Array(positions.length);
        for (var i = 0; i < n; i++) {
            var p = i * 9;
            var ax = positions[p+3] - positions[p],   ay = positions[p+4] - positions[p+1], az = positions[p+5] - positions[p+2];
            var bx = positions[p+6] - positions[p],   by = positions[p+7] - positions[p+1], bz = positions[p+8] - positions[p+2];
            var nx = ay * bz - az * by;
            var ny = az * bx - ax * bz;
            var nz = ax * by - ay * bx;
            var len = Math.sqrt(nx*nx + ny*ny + nz*nz) || 1;
            nx /= len; ny /= len; nz /= len;
            for (var v = 0; v < 3; v++) {
                normals[p + v*3]     = nx;
                normals[p + v*3 + 1] = ny;
                normals[p + v*3 + 2] = nz;
            }
        }
        return normals;
    }

    // ─── Watertight verification.
    // Counts each undirected edge (vertex pair, float-rounded to 4 decimals).
    // In a closed ("watertight") manifold every edge is shared by exactly 2
    // triangles. Returns { ok, triangles, edges, openEdges, nonManifoldEdges }.
    function checkWatertight(positions) {
        var edgeMap = new Map();
        function addEdge(ax, ay, az, bx, by, bz) {
            var r = function (n) { return n.toFixed(4); };
            var ka = r(ax) + ',' + r(ay) + ',' + r(az);
            var kb = r(bx) + ',' + r(by) + ',' + r(bz);
            var key = ka < kb ? ka + '|' + kb : kb + '|' + ka;
            edgeMap.set(key, (edgeMap.get(key) || 0) + 1);
        }
        var triangles = positions.length / 9;
        for (var i = 0; i < positions.length; i += 9) {
            addEdge(positions[i],   positions[i+1], positions[i+2],
                    positions[i+3], positions[i+4], positions[i+5]);
            addEdge(positions[i+3], positions[i+4], positions[i+5],
                    positions[i+6], positions[i+7], positions[i+8]);
            addEdge(positions[i+6], positions[i+7], positions[i+8],
                    positions[i],   positions[i+1], positions[i+2]);
        }
        var openEdges = 0, nonManifoldEdges = 0;
        edgeMap.forEach(function (count) {
            if (count === 1) openEdges++;
            else if (count > 2) nonManifoldEdges++;
        });
        return {
            ok: openEdges === 0 && nonManifoldEdges === 0,
            triangles: triangles,
            edges: edgeMap.size,
            openEdges: openEdges,
            nonManifoldEdges: nonManifoldEdges,
        };
    }

    // ─── Text → Manifold solid (for engraving / embossing labels).
    // Lifted verbatim out of /3d-calibration-cube on 2026-08-25 when
    // /3d-temperature-tower needed the same thing to stamp the temperature on
    // each segment. It was already the only working implementation; a second
    // copy is how two tools drift apart.
    //
    // Dedups coincident vertices — THREE.TextGeometry emits a non-welded soup
    // and Manifold refuses to construct from one. Result is centred on XY with
    // its base at Z=0, so the caller only has to rotate + translate onto a face.
    // Requires THREE.TextGeometry (vendor/threejs/r147/TextGeometry.js) and a
    // font from loadFont().
    function textToManifold(M, font, text, size, depth) {
        var THREE = global.THREE;
        var geom = new THREE.TextGeometry(text, {
            font: font, size: size, height: depth, curveSegments: 6, bevelEnabled: false,
        });
        geom.computeBoundingBox();
        var bb = geom.boundingBox;
        var cx = (bb.min.x + bb.max.x) / 2;
        var cy = (bb.min.y + bb.max.y) / 2;

        var position = geom.getAttribute('position').array;
        var indexAttr = geom.getIndex();
        var vertMap = new Map();
        var verts = [];
        var triVerts = [];
        function getVertex(x, y, z) {
            var k = x.toFixed(4) + ',' + y.toFixed(4) + ',' + z.toFixed(4);
            if (vertMap.has(k)) return vertMap.get(k);
            var id = verts.length / 3;
            verts.push(x, y, z);
            vertMap.set(k, id);
            return id;
        }
        if (indexAttr) {
            var ia = indexAttr.array;
            for (var i = 0; i < ia.length; i++) {
                var pIdx = ia[i] * 3;
                triVerts.push(getVertex(position[pIdx], position[pIdx + 1], position[pIdx + 2]));
            }
        } else {
            for (var j = 0; j < position.length; j += 3) {
                triVerts.push(getVertex(position[j], position[j + 1], position[j + 2]));
            }
        }
        geom.dispose();
        var m3d = new M.Mesh({
            numProp: 3,
            vertProperties: new Float32Array(verts),
            triVerts: new Uint32Array(triVerts),
        });
        return new M.Manifold(m3d).translate([-cx, -cy, 0]);
    }

    // ─── Printability inspection for GENERATED solids (Manifold input).
    //
    // checkWatertight() above answers "is this triangle soup closed?" and is
    // what the STL *readers* need. This one answers the question the STL
    // *writers* need and nobody was asking: "is what we are about to hand the
    // user a thing a printer can print?"
    //
    // Both live bugs found on 2026-08-25 were invisible to every existing
    // check because both produced perfectly watertight, perfectly closed
    // triangle soup — it was just soup describing several DETACHED lumps:
    //   • /3d-temperature-tower — the per-segment overhang arms were placed
    //     with the segment height in the Y slot instead of Z, so they marched
    //     across the bed instead of climbing the tower. 5 loose bodies.
    //     (Complaint #1491: "что это за кубики в разнобой генерирует?")
    //   • /3d-gridfinity — the stacking lip's top ledge meets the chamfer
    //     along a LINE, not a face, so the union leaves it floating.
    //     2 loose bodies, 44 non-manifold edges.
    // Neither tool's test could see it: they asserted "triangles > 0".
    //
    // Manifold answers all of this natively and cheaply (decompose() on a
    // 3k-triangle bin is sub-millisecond), so there is no reason for a
    // generating tool not to ask before it exports.
    //
    // Returns { ok, bodies, minZ, bbox, sizeMm, problems[] }.
    // `problems` are stable machine keys, not prose — they go to telemetry.
    function inspectSolid(manifold, opts) {
        opts = opts || {};
        var eps = opts.eps === undefined ? 1e-4 : opts.eps;
        var problems = [];
        var bodies = 1, bbox = null, minZ = 0, sizeMm = null;

        try {
            var parts = manifold.decompose();
            bodies = parts.length;
            for (var i = 0; i < parts.length; i++) parts[i].delete();
        } catch (e) {
            problems.push('decompose_failed');
        }

        try {
            bbox = manifold.boundingBox();
            minZ = bbox.min[2];
            sizeMm = [bbox.max[0] - bbox.min[0], bbox.max[1] - bbox.min[1], bbox.max[2] - bbox.min[2]];
        } catch (e) {
            problems.push('bbox_failed');
        }

        // A generated model is one connected lump. More than one means parts
        // came adrift — the slicer will treat them as separate objects and the
        // user gets exactly the "scattered cubes" they reported.
        if (bodies > 1) problems.push('multi_body');
        // Anything under the bed is unprintable: the slicer either clips it or
        // refuses. Generated models are authored Z-up with the base at Z=0.
        if (bbox && minZ < -eps) problems.push('below_plate');
        if (bbox && sizeMm && (sizeMm[0] <= 0 || sizeMm[1] <= 0 || sizeMm[2] <= 0)) problems.push('degenerate');

        return {
            ok: problems.length === 0,
            bodies: bodies,
            minZ: minZ,
            bbox: bbox,
            sizeMm: sizeMm,
            problems: problems,
        };
    }

    // Inspect + report. Never mutates and never blocks the download: a guard
    // that silently drops geometry the user can see on screen is a second bug,
    // not a fix. The suite is the hard gate (tests/playwright/3d-printability
    // sweeps every tool and variant); this is the net for the parameter
    // combinations the sweep does not enumerate.
    function assertPrintable(manifold, ctx) {
        ctx = ctx || {};
        var report = inspectSolid(manifold, ctx);
        if (!report.ok) {
            var payload = {
                problems: report.problems.join(','),
                bodies: report.bodies,
                minZ: Math.round(report.minZ * 100) / 100,
            };
            if (ctx.variant) payload.variant = ctx.variant;
            if (ctx.slug) aeCritical(ctx.slug, 'model_unprintable', payload);
            if (global.console && console.warn) {
                console.warn('[3d] model failed printability check:', report.problems.join(', '), report);
            }
        }
        return report;
    }

    // ─── Generic helpers
    function downloadBlob(blob, filename) {
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        setTimeout(function () {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }, 1000);
    }

    function formatSize(mm) {
        if (mm >= 10) return (mm / 10).toFixed(1) + ' cm';
        return mm.toFixed(1) + ' mm';
    }

    function formatNumber(n, digits) {
        if (n === null || n === undefined || isNaN(n)) return '—';
        return Number(n).toLocaleString(global.__LOCALE || 'en', {
            minimumFractionDigits: digits || 0,
            maximumFractionDigits: digits || 0,
        });
    }

    // ─── Three.js scene helpers (require window.THREE loaded)
    function makeViewer(container, opts) {
        if (typeof global.THREE === 'undefined') {
            throw new Error('three.js not loaded — include /vendor/threejs/152/three.min.js first');
        }
        // Capability gate BEFORE `new THREE.WebGLRenderer()`. The renderer's
        // synchronous throw lands as `Error: Error creating WebGL context`
        // with no user UI (Sentry TIMBRICA-FRONTEND-4B). Pre-flighting here
        // gives every T3.makeViewer caller a typed err.code = WEBGL_UNAVAILABLE
        // with .expected = true so the global preventDefault() listener
        // absorbs it and the caller can show a localized "3D viewer requires
        // WebGL" toast instead of a silent-broken tool.
        if (typeof global.__safeWebGL === 'function') global.__safeWebGL.assert();
        opts = opts || {};
        var THREE = global.THREE;
        var width = container.clientWidth || 600;
        var height = container.clientHeight || 400;

        var scene = new THREE.Scene();
        scene.background = opts.background ? new THREE.Color(opts.background) : null;

        var camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 5000);
        camera.position.set(80, 80, 120);

        /* The canvas is ours, created BEFORE the renderer, for one reason: three
           r147 attaches its webglcontextlost/restored listeners to the canvas
           before it has a context, and when context creation then fails — iOS
           over its live-context budget hands out a context that is born lost,
           so initGLContext() throws on the first getParameter — the constructor
           throws with those listeners still attached and `info` never created.
           When the device later frees a slot, webglcontextrestored fires on that
           orphan canvas and three's handler reads info.autoReset of undefined
           (Sentry JC, /3d-photo-to-model, iOS). A capture-phase listener that we
           register FIRST keeps the event from ever reaching a renderer that never
           came up; once construction succeeds it steps aside. */
        var canvas = document.createElement('canvas');
        var rendererUp = false;
        canvas.addEventListener('webglcontextrestored', function (ev) {
            if (!rendererUp) ev.stopImmediatePropagation();
        }, true);
        var renderer;
        try {
            renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: !opts.background });
        } catch (e) {
            try { canvas.width = 0; canvas.height = 0; } catch (_) {}
            var err = new Error('WebGL context could not be created: ' + ((e && e.message) || e));
            err.code = 'WEBGL_UNAVAILABLE';
            err.expected = true;
            err.cause = e;
            throw err;
        }
        rendererUp = true;
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.setSize(width, height);
        container.appendChild(renderer.domElement);

        // Lighting
        scene.add(new THREE.AmbientLight(0xffffff, 0.55));
        var keyLight = new THREE.DirectionalLight(0xffffff, 0.85);
        keyLight.position.set(80, 120, 100);
        scene.add(keyLight);
        var fillLight = new THREE.DirectionalLight(0xb8c7ff, 0.45);
        fillLight.position.set(-100, 40, -80);
        scene.add(fillLight);

        // Grid helper
        if (opts.grid !== false) {
            var grid = new THREE.GridHelper(200, 20, 0x446688, 0x223344);
            grid.material.opacity = 0.35;
            grid.material.transparent = true;
            scene.add(grid);
        }

        // Controls
        var controls = null;
        if (global.THREE.OrbitControls) {
            controls = new global.THREE.OrbitControls(camera, renderer.domElement);
            controls.enableDamping = true;
            controls.dampingFactor = 0.08;
        }

        function onResize() {
            var w = container.clientWidth || 600;
            var h = container.clientHeight || 400;
            camera.aspect = w / h;
            camera.updateProjectionMatrix();
            renderer.setSize(w, h);
        }
        window.addEventListener('resize', onResize);

        var running = true;
        var gl = renderer.getContext();
        function animate() {
            if (!running) return;
            // rAF FIRST: a throw inside render() (e.g. context dying mid-frame in
            // vendored three.js) must not kill the loop forever — the viewer
            // self-heals when the browser restores the context.
            requestAnimationFrame(animate);
            // iOS/WebKit can evict the GL context between frames under memory
            // pressure (ONNX depth inference on phones); webglcontextlost is
            // dispatched ASYNC, so three.js r147's internal guard may not have
            // flipped yet — its unguarded getShaderInfoLog(...).trim() then throws
            // "null is not an object" on first-compile of a new material
            // (TIMBRICA-FRONTEND-AQ). isContextLost() is the only synchronous
            // truth: skip the frame instead.
            if (gl.isContextLost()) return;
            if (controls) controls.update();
            renderer.render(scene, camera);
        }
        animate();

        // ── Кадрирование по РЕАЛЬНОЙ проекции, а не по размеру в мире ────────
        //
        // Было: dist = maxDim * 1.8 вдоль (0.7, 0.7, 1). Две беды, обе видны
        // только на замере (RU-воркти, 26.08, восемь случаев):
        //
        //   1. maxDim — самая длинная сторона В МИРЕ, а на экран объект
        //      проецируется УКОРОЧЕННЫМ: мы смотрим на него сверху-сбоку.
        //      Для компактного тела разница мала (куб: fillY 0.759), для
        //      плоской плитки — вдвое (рельеф из фото: fillY 0.373).
        //   2. Учитывался только ВЕРТИКАЛЬНЫЙ угол обзора. Канвас вьюпорта на
        //      ноутбуке 814×360 — соотношение 2.26; для широкого объекта
        //      вертикаль вообще не связывающая ось.
        //
        // Стало: ставим камеру по тому же направлению (вид не меняем — это
        // узнаваемый ракурс всех 3d-тулов), затем ИЗМЕРЯЕМ проекцию восьми
        // углов bbox и подгоняем дистанцию. Проекция масштабируется обратно
        // расстоянию, поэтому dist *= fill/target сходится за 2-3 шага; цикл
        // ограничен, выход по достижении цели.
        //
        // `fill` — это САМЫЙ ДАЛЬНИЙ угол в NDC, а не полуразмах проекции.
        // Разница не косметическая: в перспективе проекция коробки НЕ
        // симметрична относительно своего центра (ближние углы уезжают дальше),
        // поэтому подгонка полуразмаха под 0.86 всё равно давала обрез — на
        // замере 26.08 обрезало три случая из восьми при формально «86%».
        // По дальнему углу цель < 1 обрезать не может по построению.
        var FIT_TARGET = 0.86;

        // Самый дальний угол объекта в NDC при ТЕКУЩЕЙ камере (1.0 = край кадра).
        function projectedFill(box) {
            camera.updateMatrixWorld(true);
            // .project() читает matrixWorldInverse, а его обновляет рендерер
            // раз в кадр — внутри итерации он протухший, поэтому строим сами.
            camera.matrixWorldInverse.copy(camera.matrixWorld).invert();
            var mn = box.min, mx = box.max;
            var worst = 0;
            var v = new THREE.Vector3();
            for (var i = 0; i < 8; i++) {
                v.set((i & 1) ? mx.x : mn.x, (i & 2) ? mx.y : mn.y, (i & 4) ? mx.z : mn.z);
                v.project(camera);
                worst = Math.max(worst, Math.abs(v.x), Math.abs(v.y));
            }
            return worst;
        }

        function fitCamera(object, opts) {
            var box = new THREE.Box3().setFromObject(object);
            if (box.isEmpty()) return;
            var size = box.getSize(new THREE.Vector3());
            var center = box.getCenter(new THREE.Vector3());
            var maxDim = Math.max(size.x, size.y, size.z) || 1;
            var target = (opts && opts.fill) || FIT_TARGET;

            // Направление взгляда. По умолчанию — узнаваемый ракурс всех
            // 3d-тулов. Но fitCamera зовут ПОВТОРНО: /3d-photo-to-model
            // пересобирает меш на каждое движение ползунка и каждый раз
            // кадрирует заново, то есть стирает поворот, который человек
            // только что сделал мышью. keepDirection сохраняет его ракурс и
            // трогает только дистанцию — рамка остаётся правильной, вид
            // остаётся его.
            var dir;
            if (opts && opts.keepDirection && controls) {
                dir = camera.position.clone().sub(controls.target);
                if (dir.lengthSq() < 1e-9) dir = new THREE.Vector3(0.7, 0.7, 1);
                dir.normalize();
            } else {
                dir = new THREE.Vector3(0.7, 0.7, 1).normalize();
            }
            var dist = maxDim * 2.5;      // ≈ прежняя стартовая точка

            for (var it = 0; it < 5; it++) {
                camera.position.copy(center).addScaledVector(dir, dist);
                camera.near = Math.max(dist / 1000, 0.01);
                camera.far = dist * 10 + maxDim * 10;
                camera.updateProjectionMatrix();
                camera.lookAt(center);    // controls.update() поставит то же самое
                var fill = projectedFill(box);
                if (!isFinite(fill) || fill <= 0) break;
                if (Math.abs(fill - target) < 0.01) break;
                // Слишком резкий шаг на первой итерации выкидывает камеру за
                // пределы near/far, поэтому множитель зажат.
                dist *= Math.min(4, Math.max(0.25, fill / target));
            }

            if (controls) {
                controls.target.copy(center);
                controls.update();
            }
        }

        function dispose() {
            running = false;
            window.removeEventListener('resize', onResize);
            if (controls && controls.dispose) controls.dispose();
            renderer.dispose();
            if (renderer.domElement && renderer.domElement.parentNode) {
                renderer.domElement.parentNode.removeChild(renderer.domElement);
            }
        }

        return { scene: scene, camera: camera, renderer: renderer, controls: controls, fitCamera: fitCamera, dispose: dispose };
    }

    // Mesh factory from parsed STL
    function meshFromSTL(parsed, opts) {
        opts = opts || {};
        var THREE = global.THREE;
        var geom = new THREE.BufferGeometry();
        geom.setAttribute('position', new THREE.BufferAttribute(parsed.positions, 3));
        if (parsed.normals) geom.setAttribute('normal', new THREE.BufferAttribute(parsed.normals, 3));
        else geom.computeVertexNormals();
        geom.computeBoundingBox();
        geom.computeBoundingSphere();
        var material = new THREE.MeshStandardMaterial({
            color: opts.color || 0x7aa7ff,
            metalness: opts.metalness != null ? opts.metalness : 0.15,
            roughness: opts.roughness != null ? opts.roughness : 0.45,
            flatShading: !!opts.flat,
        });
        var mesh = new THREE.Mesh(geom, material);
        // center it
        var center = new THREE.Vector3();
        geom.boundingBox.getCenter(center);
        mesh.position.set(-center.x, -center.y, -center.z);
        return mesh;
    }

    // ─── Telemetry helpers (thin wrapper over window.__ae / __aeCritical)
    //
    // Funnel shape for the 3D tools:
    //   tool_open       — auto on init(slug); fires once per page load
    //   tool_start      — first user interaction (click/input); fires once
    //   tool_stage      — major milestones (upload, parse, render, download)
    //   tool_complete   — primary value delivered (download OR preview shown)
    //   tool_error      — any recoverable failure (with reason code)
    //   feature_use     — throttled knob twiddling (material picker, etc.)
    //   tool_stalled    — emitted if an operation exceeds the timeout budget

    function ae(slug, event, payload) {
        try {
            if (typeof global.__ae !== 'function') return;
            var body = Object.assign({ tool: slug }, payload || {});
            global.__ae(event, body);
        } catch (e) { /* ignore */ }
    }

    function aeCritical(slug, event, payload) {
        try {
            if (typeof global.__aeCritical !== 'function') return;
            var body = Object.assign({ tool: slug }, payload || {});
            global.__aeCritical(event, body);
        } catch (e) { /* ignore */ }
    }

    function handleError(err, ctx) {
        try {
            if (typeof global.__handleError === 'function') return global.__handleError(err, ctx);
            console.error('[3d]', ctx, err);
        } catch (e) {}
    }

    /**
     * init(slug) — call once per tool page load. Fires `tool_open` with
     * perf timings (page ready → core ready) and sets up lazy listeners
     * that auto-fire `tool_start` on the first user interaction.
     * Returns a handle with helpers tied to this tool slug.
     */
    function init(slug) {
        var started = false;
        var openTs = Date.now();
        var nav = (typeof performance !== 'undefined' && performance.timing)
            ? performance.timing : null;
        var pageReadyMs = nav ? (openTs - nav.navigationStart) : null;

        ae(slug, 'tool_open', { ready_ms: pageReadyMs });

        function fireStart(kind) {
            if (started) return;
            started = true;
            ae(slug, 'tool_start', { k: kind, ttfi_ms: Date.now() - openTs });
        }

        // Attach once-listeners for the first interaction
        var rootSel = '.lg-app';
        var root = document.querySelector(rootSel);
        if (root) {
            ['click', 'input', 'change'].forEach(function (t) {
                root.addEventListener(t, function handler(e) {
                    var tag = (e.target && e.target.tagName || '').toLowerCase();
                    fireStart(tag + ':' + t);
                    root.removeEventListener(t, handler, { once: true });
                }, { once: true });
            });
        }

        // Perf: if three.js was needed for this tool, emit wasm_load on window load
        if (typeof global.THREE !== 'undefined') {
            ae(slug, 'wasm_load', { lib: 'three', ready_ms: pageReadyMs });
        }

        return {
            slug: slug,
            stage: function (name, data) { ae(slug, 'tool_stage', Object.assign({ stage: name }, data || {})); },
            complete: function (data) { ae(slug, 'tool_complete', data || {}); },
            error: function (reason, data) { ae(slug, 'tool_error', Object.assign({ reason: reason }, data || {})); },
            stalled: function (data) { aeCritical(slug, 'tool_stalled', data || {}); },
            feature: function (k, data) { ae(slug, 'feature_use', Object.assign({ k: k }, data || {})); },
        };
    }

    /**
     * throttle(fn, ms) — leading-edge throttle for noisy feature_use events.
     * Useful for inputs where a single slider drag fires 50+ change events.
     */
    function throttle(fn, ms) {
        var last = 0;
        return function () {
            var now = Date.now();
            if (now - last < ms) return;
            last = now;
            return fn.apply(this, arguments);
        };
    }

    /**
     * withStalledWatchdog(operation, timeoutMs, slug, data) — run `operation()`
     * and emit `tool_stalled` if it hasn't settled within timeoutMs. The
     * operation must return a Promise (or thenable).
     */
    function withStalledWatchdog(op, timeoutMs, slug, data) {
        var settled = false;
        var timer = setTimeout(function () {
            if (!settled) aeCritical(slug, 'tool_stalled', Object.assign({ timeout_ms: timeoutMs }, data || {}));
        }, timeoutMs);
        return Promise.resolve().then(op).finally(function () {
            settled = true;
            clearTimeout(timer);
        });
    }

    // ─── Manifold WASM lazy loader (ESM module).
    // Exposes Manifold + CrossSection from manifold-3d@2.5.1 via dynamic import.
    // Called at most once per page; result cached on window.__Manifold.
    var _manifoldBase = '/vendor/manifold-3d/';
    var _manifoldPromise = null;
    function loadManifold(urlBase) {
        if (window.__Manifold) return Promise.resolve(window.__Manifold);
        if (_manifoldPromise) return _manifoldPromise;
        urlBase = urlBase || _manifoldBase;
        _manifoldPromise = import(urlBase + 'manifold.js')
            .then(function (mod) {
                return mod.default({ locateFile: function () { return urlBase + 'manifold.wasm'; } });
            })
            .then(function (wasm) {
                wasm.setup();
                window.__Manifold = wasm;
                return wasm;
            })
            .catch(function (err) {
                _manifoldPromise = null; // allow retry
                throw err;
            });
        return _manifoldPromise;
    }

    // ─── Font loader (helvetiker). One font cached across tool pages.
    var _fontPromise = null;
    function loadFont(url) {
        if (window.__helvetikerFont) return Promise.resolve(window.__helvetikerFont);
        if (_fontPromise) return _fontPromise;
        _fontPromise = new Promise(function (resolve, reject) {
            if (typeof global.THREE === 'undefined' || !global.THREE.FontLoader) {
                return reject(new Error('FontLoader not loaded — include /vendor/threejs/r147/FontLoader.js first'));
            }
            var loader = new global.THREE.FontLoader();
            loader.load(url, function (font) {
                window.__helvetikerFont = font;
                resolve(font);
            }, undefined, reject);
        });
        return _fontPromise;
    }

    // ─── Manifold → three.js bridge.
    // Converts a Manifold Mesh (flat vertProperties + triVerts arrays) to a
    // THREE.BufferGeometry with position + normal attributes and (optional)
    // per-face IDs for selective re-colouring.
    function manifoldMeshToGeometry(manifold) {
        var THREE = global.THREE;
        var mesh = manifold.getMesh();
        var vp = mesh.vertProperties;   // Float32Array: x,y,z [, nx,ny,nz [, r,g,b]]
        var tv = mesh.triVerts;          // Uint32Array: indices into vert list
        var stride = mesh.numProp;       // usually 3 (pos only)
        var positions = new Float32Array(tv.length * 3);
        for (var i = 0; i < tv.length; i++) {
            var idx = tv[i];
            positions[i * 3]     = vp[idx * stride];
            positions[i * 3 + 1] = vp[idx * stride + 1];
            positions[i * 3 + 2] = vp[idx * stride + 2];
        }
        var geom = new THREE.BufferGeometry();
        geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geom.computeVertexNormals();
        return geom;
    }

    // ─── Geometry → binary STL (from THREE.BufferGeometry).
    function geometryToSTL(geometry) {
        var posAttr = geometry.getAttribute('position');
        var normAttr = geometry.getAttribute('normal');
        var positions = posAttr.array;
        var normals = normAttr ? normAttr.array : computeNormals(positions);
        return writeBinarySTL(positions, normals);
    }

    // ─── Chord-major extrude: take a 2D polygon (array of {x,y}) and extrude
    //     it along Z. Returns positions/normals for a closed prism. Used as
    //     manifold-free fallback when manifold isn't warranted.
    function extrudePolygon(polygon, height) {
        var positions = [], normals = [];
        var n = polygon.length;
        // Bottom cap (normal -Z, fan triangulation from vertex 0)
        for (var i = 1; i < n - 1; i++) {
            var p0 = polygon[0], p1 = polygon[i], p2 = polygon[i + 1];
            positions.push(p0.x, 0, p0.y, p2.x, 0, p2.y, p1.x, 0, p1.y);
            for (var k = 0; k < 3; k++) normals.push(0, -1, 0);
        }
        // Top cap (normal +Z)
        for (var i2 = 1; i2 < n - 1; i2++) {
            var q0 = polygon[0], q1 = polygon[i2], q2 = polygon[i2 + 1];
            positions.push(q0.x, height, q0.y, q1.x, height, q1.y, q2.x, height, q2.y);
            for (var k2 = 0; k2 < 3; k2++) normals.push(0, 1, 0);
        }
        // Sides
        for (var j = 0; j < n; j++) {
            var a = polygon[j], b = polygon[(j + 1) % n];
            var dx = b.x - a.x, dz = b.y - a.y;
            var len = Math.sqrt(dx * dx + dz * dz) || 1;
            var nx = dz / len, nz = -dx / len;
            positions.push(a.x, 0, a.y, b.x, 0, b.y, b.x, height, b.y);
            positions.push(a.x, 0, a.y, b.x, height, b.y, a.x, height, a.y);
            for (var kk = 0; kk < 6; kk++) normals.push(nx, 0, nz);
        }
        return {
            positions: new Float32Array(positions),
            normals: new Float32Array(normals),
        };
    }

    // ─── ONNX Runtime Web lazy loader.
    // One tool loads the runtime; the result is memoised on window.__ort so
    // subsequent tools on the same session don't refetch it.
    //
    // wasmPaths + numThreads + proxy are configured here for a non-isolated
    // page. Without the explicit numThreads=1, ORT tries to spin up pthread
    // workers; the JSEP build then fails with a cryptic LinkError on
    // `Import #126 "a" "tb"` — `tb` is a pthread thunk the wasm expects in
    // the imports table that isn't bound when the page lacks COOP/COEP
    // (crossOriginIsolated === false). Same pattern we use in
    // ai-music-classifier.js / ai-upscale-core.js.
    //
    // ORT 1.18 is the lane of a browser that cannot RESERVE the shared 4 GiB
    // memory the current build imports (WebKit before 18: «no available backend
    // found. ERR: [wasm] RangeError: Out of memory» at the first session). The
    // owner (window.__ortBuild) learns it from the first failure —
    // loadDepthSession below tells it — and answers the legacy build from then
    // on. Only with SIMD: this pipeline was never measured without it.
    function _ortLegacy() {
        var b = window.__ortBuild, L = null;
        try { L = (b && typeof b.legacy === 'function') ? b.legacy() : null; } catch (_) {}
        return (L && L.simd) ? L : null;
    }
    function _ortIsLegacy(o) {
        try { return /^1\.18\./.test(String((o && o.env && o.env.versions && (o.env.versions.web || o.env.versions.common)) || '')); }
        catch (_) { return false; }
    }
    var _ortPromise = null;
    function loadONNX(url) {
        if (window.__ort && window.__ort.InferenceSession) return Promise.resolve(window.__ort);
        if (_ortPromise) return _ortPromise;
        var legacy = url ? null : _ortLegacy();
        url = url || (legacy ? legacy.url : '/vendor/onnxruntime/ort.min.js');
        _ortPromise = new Promise(function (resolve, reject) {
            var s = document.createElement('script');
            s.src = url;
            s.async = true;
            s.onload = function () {
                if (window.ort && window.ort.InferenceSession) {
                    try {
                        // Absolute URL so ORT-Web's internal `import()` of
                        // jsep / SIMD-threaded helpers resolves correctly
                        // even when ort.min.js is later loaded into a blob
                        // worker context. Fix mirrors apollo-worker.js:78-91
                        // and clap-worker.js (same class fired 10× on 3d-*
                        // tools 2026-06-11/12 telemetry).
                        var _wp = legacy ? legacy.wasmBase : '/vendor/onnxruntime/';
                        if (typeof location !== 'undefined' && location.origin) _wp = location.origin + _wp;
                        window.ort.env.wasm.wasmPaths = _wp;
                        // the legacy directory has no threaded wasm
                        var isolated = !legacy && (typeof crossOriginIsolated !== 'undefined') && crossOriginIsolated;
                        window.ort.env.wasm.numThreads = isolated
                            ? Math.min(navigator.hardwareConcurrency || 4, 4)
                            : 1;
                        window.ort.env.wasm.proxy = false;
                    } catch (_) {}
                    window.__ort = window.ort;
                    resolve(window.ort);
                } else {
                    reject(new Error('ort.min.js loaded but window.ort missing'));
                }
            };
            s.onerror = function () { reject(new Error('Failed to load ort.min.js')); };
            document.head.appendChild(s);
        }).catch(function (err) {
            _ortPromise = null;
            throw err;
        });
        return _ortPromise;
    }

    // Probe whether WebGPU is actually usable on this device. `navigator.gpu`
    // existing means the API is exposed, but `requestAdapter()` can still
    // return null on systems without a working GPU (Linux + llvmpipe,
    // remote desktops, blocklisted drivers). We must NOT request webgpu
    // execution if the adapter is unavailable — once ORT has loaded the
    // JSEP-flavoured wasm and tried to bind WebGPU, a fall-back to wasm
    // re-uses the tainted runtime and throws the same LinkError.
    var _webgpuProbe = null;
    function probeWebGPU() {
        if (_webgpuProbe) return _webgpuProbe;
        _webgpuProbe = (async function () {
            if (typeof navigator === 'undefined' || !navigator.gpu) return false;
            try {
                var adapter = await navigator.gpu.requestAdapter();
                return !!adapter;
            } catch (_) { return false; }
        })();
        return _webgpuProbe;
    }

    /**
     * loadDepthSession(urls, onProgress) — fetches + creates an ONNX
     * InferenceSession for a single-input depth model (e.g. DepthAnything-V2
     * Small quantised).
     *
     * `urls` can be a single string or an array of URLs tried in order; if
     * the first URL 404s (e.g. the file isn't vendored on our VPS yet) the
     * loader falls back to the next URL (e.g. the upstream CDN).
     *
     * Emits {loaded, total} via onProgress during download.
     * Caches the session on window.__depthSession for reuse.
     */
    var _depthSessionPromise = null;
    function loadDepthSession(urls, onProgress) {
        if (window.__depthSession) return Promise.resolve(window.__depthSession);
        // Cache the in-flight promise so concurrent callers (preload on page
        // open + re-trigger on file upload) share ONE download. Without this,
        // RU users were fetching the model twice — once from /vendor/ and once
        // from the upstream CDN — because the second call's fallback chain
        // kicked in before the first call finished.
        if (_depthSessionPromise) return _depthSessionPromise;
        var raw = Array.isArray(urls) ? urls : [urls];
        var seen = {};
        var list = [];
        for (var i = 0; i < raw.length; i++) {
            var u = raw[i];
            if (!u || seen[u]) continue;
            seen[u] = 1;
            list.push(u);
        }
        _depthSessionPromise = loadONNX().then(function (ort) {
            function tryFetch(i) {
                if (i >= list.length) return Promise.reject(new Error('all depth model URLs failed'));
                return fetch(list[i]).then(function (resp) {
                    if (!resp.ok) throw new Error('HTTP ' + resp.status);
                    var total = parseInt(resp.headers.get('Content-Length') || '0', 10);
                    if (!onProgress || !resp.body) return resp.arrayBuffer();
                    var reader = resp.body.getReader();
                    var chunks = [], received = 0;
                    function pump() {
                        return reader.read().then(function (r) {
                            if (r.done) {
                                var buf = new Uint8Array(received);
                                var off = 0;
                                for (var k = 0; k < chunks.length; k++) { buf.set(chunks[k], off); off += chunks[k].length; }
                                return buf.buffer;
                            }
                            chunks.push(r.value);
                            received += r.value.length;
                            try { onProgress(received, total); } catch (e) {}
                            return pump();
                        });
                    }
                    return pump();
                }).catch(function () { return tryFetch(i + 1); });
            }
            return tryFetch(0).then(function (buf) {
                // Force WASM-only execution for depth-model sessions.
                //
                // Background: 1.24.3's `ort.min.js` is a JSEP-flavoured "all"
                // bundle. Whenever providers includes 'webgpu', the runtime
                // partially binds JSEP imports into the wasm imports table.
                // On a class of devices where `navigator.gpu` exposes an
                // adapter but the JSEP-side init fails halfway (intermittent
                // driver / Chrome-version bug, no clean public repro), the
                // wasm instantiate then fails with `LinkError: Import #126
                // "a" "tb" requires a callable` — `tb` is a pthread thunk
                // the JSEP layer was supposed to inject. It's not enough to
                // skip 'webgpu' when adapter probe fails: even on systems
                // where WebGPU "looks fine" (requestAdapter succeeds),
                // the LinkError still fires because the device-side init is
                // what trips. Closed Sentry-reported regression on
                // /ru/3d-photo-to-model 2026-05-08.
                //
                // Until ORT's JSEP path stabilises, the depth pipeline
                // pins to ['wasm']. Real-world inference is ~9 s on this
                // model vs ~3 s WebGPU — acceptable for a one-shot user
                // upload. AIUpscaleCore / RIFE keep their own WebGPU paths
                // because they have a much more elaborate compute-shader
                // probe (see AIUpscaleCore.probe) before opting in.
                function create(o) {
                    return o.InferenceSession.create(buf, {
                        executionProviders: ['wasm'],
                        graphOptimizationLevel: 'all',
                    });
                }
                return create(ort).catch(function (e) {
                    // Not this device running out of memory — the reservation
                    // failure (see _ortLegacy). The runtime that failed is dropped
                    // (a failed init is cached by it), 1.18 takes its place after a
                    // pause: an immediate retry meets the same address space. The
                    // model bytes are still ours — nothing was handed over.
                    var b = window.__ortBuild;
                    if (!buf.byteLength || !(b && b.initFailed
                        && b.initFailed(e, { legacy: _ortIsLegacy(ort) ? 'legacy' : '', from: 'depth' }))) throw e;
                    try { delete window.ort; } catch (_) { try { window.ort = undefined; } catch (__) {} }
                    window.__ort = null;
                    _ortPromise = null;
                    return new Promise(function (r) { setTimeout(r, 1000); })
                        .then(function () { return loadONNX(); })
                        .then(create);
                }).then(function (session) {
                    session.__provider = 'wasm';
                    return session;
                });
            }).then(function (session) {
                window.__depthSession = session;
                return session;
            });
        }).catch(function (err) {
            _depthSessionPromise = null; // allow retry after failure
            throw err;
        });
        return _depthSessionPromise;
    }

    /**
     * isolateSubjectDepth(depth) — отдать ВЕСЬ ход рельефа переднему плану.
     *
     * Карта глубины нормируется по всему кадру (min/max в runDepthInference),
     * поэтому далёкий фон растягивает шкалу, а предмет сжимается в её часть.
     * Замер на четырёх настоящих снимках настоящей моделью (26.08, пресет
     * «Портрет», 12 мм, слой 0.2 мм) — доля хода, достающаяся предмету:
     * 0.191 скейтер · 0.336 портрет · 0.26 толпа. То есть 11–20 слоёв печати
     * на весь предмет: это не «некрасиво», это неразрешимо принтером.
     *
     * Здесь порог Оцу делит ближнее/дальнее, ближняя часть растягивается на
     * всю шкалу (по перцентилям 2..98, чтобы одиночный выброс не съел ход),
     * фон прижимается к основанию. Скейтер: 11 → 32 слоя.
     *
     * ⚠️ ВКЛЮЧАТЬ ТОЛЬКО ПО ПРОСЬБЕ ЧЕЛОВЕКА, и вот почему. Тот же замер по
     * средней крутизне ВСЕЙ карты (то есть сколько всего поверхностной
     * информации осталось): скейтер −24%, портрет −21%, пейзаж −37%. Выигрыш
     * у предмета оплачен сплющенной серединой — на портрете в цеху пропадают
     * полки и станки, девочка становится гладким бугром. Проверено и глазами
     * на рендерах, не только числом.
     *
     * ⚠️ Автоматически включать НЕЛЬЗЯ: признака «в кадре есть отделимый
     * предмет» не нашлось. Разделимость Оцу (eta = дисперсия между классами /
     * общая) у всех четырёх снимков 0.747…0.793 — пейзаж без переднего плана
     * неотличим от скейтера. Проверено, отвергнуто.
     *
     * ⚠️ Также проверены и отвергнуты как автоматические: растяжка по
     * перцентилям 2..98 (не вредит нигде, но +9…17% глазу не видны),
     * smoothstep и линейный контраст ×1.8 в духе /3d-lithophane-generator
     * (помогают снимкам с предметом, но портят открытые сцены: пейзаж −12% и
     * −22%). Числа — в памяти `tool_3d_photo_to_model_2026_08_26`.
     */
    function isolateSubjectDepth(depth) {
        if (!depth || !depth.length) return depth;
        var B = 64, hist = new Array(B), i;
        for (i = 0; i < B; i++) hist[i] = 0;
        for (i = 0; i < depth.length; i++) {
            var b = Math.floor(depth[i] * B);
            hist[b < 0 ? 0 : (b >= B ? B - 1 : b)]++;
        }
        var total = depth.length, sum = 0;
        for (i = 0; i < B; i++) sum += i * hist[i];
        var sumB = 0, wB = 0, best = -1, thr = 0;
        for (i = 0; i < B; i++) {
            wB += hist[i]; if (!wB) continue;
            var wF = total - wB; if (!wF) break;
            sumB += i * hist[i];
            var mB = sumB / wB, mF = (sum - sumB) / wF;
            var v = wB * wF * (mB - mF) * (mB - mF);
            if (v > best) { best = v; thr = i; }
        }
        var cut = (thr + 1) / B;
        var near = [];
        for (i = 0; i < depth.length; i++) if (depth[i] >= cut) near.push(depth[i]);
        // Меньше 2% кадра — это не предмет, а блик или пылинка на объективе.
        // Растянуть шкалу по ним значит выбросить всю сцену.
        if (near.length < depth.length * 0.02) return depth;
        near.sort(function (a, b2) { return a - b2; });
        var lo = near[Math.floor(near.length * 0.02)];
        var hi = near[Math.floor(near.length * 0.98)];
        if (!(hi - lo > 1e-6)) return depth;
        var out = new Float32Array(depth.length);
        for (i = 0; i < depth.length; i++) {
            var t = (depth[i] - lo) / (hi - lo);
            out[i] = t < 0 ? 0 : (t > 1 ? 1 : t);
        }
        return out;
    }

    /**
     * imageToDepthTensor(image, size) — prepares a 1×3×H×W Float32 tensor
     * in ImageNet normalisation (mean [0.485,0.456,0.406], std [0.229,0.224,0.225]).
     * Returns { tensor, origW, origH, sourceCanvas }.
     */
    function imageToDepthTensor(image, size) {
        size = size || 518; // DepthAnything default
        var canvas = document.createElement('canvas');
        canvas.width = size; canvas.height = size;
        var ctx = canvas.getContext('2d');
        // Center-fit with letterboxing on zero-padding is what most depth
        // models expect. DepthAnything is fairly robust to stretching so
        // we stretch to the full square for simplicity + speed.
        ctx.drawImage(image, 0, 0, size, size);
        var data = ctx.getImageData(0, 0, size, size).data;
        var tensor = new Float32Array(3 * size * size);
        var mean = [0.485, 0.456, 0.406];
        var std = [0.229, 0.224, 0.225];
        for (var i = 0, p = 0; i < data.length; i += 4, p++) {
            tensor[p]                     = (data[i]     / 255 - mean[0]) / std[0]; // R
            tensor[p + size * size]       = (data[i + 1] / 255 - mean[1]) / std[1]; // G
            tensor[p + size * size * 2]   = (data[i + 2] / 255 - mean[2]) / std[2]; // B
        }
        return {
            tensor: tensor,
            origW: image.naturalWidth || image.width,
            origH: image.naturalHeight || image.height,
            sourceCanvas: canvas,
            size: size,
        };
    }

    /**
     * runDepthInference(session, prep) — runs the ONNX session on a prepared
     * tensor and returns { depth: Float32Array, w, h } normalised to [0,1]
     * where 1 = closest to camera.
     */
    // ── One session.run() at a time. Not negotiable, so it is owned here. ────
    //
    // ORT-Web permits exactly ONE session.run() in flight per module instance.
    // The shipped guard (public/vendor/onnxruntime/…jsep.mjs) reads:
    //     const da=a=>async(...b)=>{try{
    //         if(f.$c) throw Error("Session already started");
    //         const d=f.$c={Nd:b[0],errors:[]}, c=await a(...b);
    //         if(f.$c!==d) throw Error("Session mismatch");
    //         ...} finally { f.$c=null }};
    // Note the `finally`: the SECOND (throwing) call clears the guard object
    // the FIRST one is still holding, so a single overlap kills BOTH runs.
    // public/js/supertonic-worker.js documents the same trap and serialises for
    // the same reason; this is that fix for the main-thread depth pipeline.
    //
    // How it was reached here (measured, RU shard, 30 d to 26.08):
    // /3d-photo-to-model shows its sliders the moment the photo decodes and
    // then runs ~9.5 s of WASM inference (infer_ms 9569 in the trace behind
    // comment #1492). Its guard was `if (!force && lastDepth)` — a check on the
    // RESULT, and the result is only assigned when inference finishes, so any
    // slider touched during those 9.5 s started a second full run. 63 sessions
    // "Session already started" + 63 "Session mismatch" — always paired,
    // because both runs die — and 62 of those 63 (98.4%) had emitted a slider
    // event. The user was then told "Depth estimation failed. Reload the page
    // or try a different photo": 101 sessions, ~6% of everyone who uploaded,
    // advised to throw away a photo that was never the problem.
    //
    // Queue rather than reject: a caller asking for depth twice wants depth
    // twice, and /stereo-pictures shares window.__depthSession with
    // /3d-photo-to-model, so "whoever is second loses" would be a coin flip
    // between two tools. Chained on BOTH settle paths so one failed run cannot
    // wedge the queue.
    var _depthChain = Promise.resolve();
    function runDepthInference(session, prep) {
        var run = function () { return _runDepthInferenceNow(session, prep); };
        var out = _depthChain.then(run, run);
        // The queue must survive a rejection; the CALLER must still see it.
        _depthChain = out.then(function () {}, function () {});
        return out;
    }
    function _runDepthInferenceNow(session, prep) {
        var ort = window.__ort || window.ort;
        if (!ort) throw new Error('ORT not loaded');
        var input = new ort.Tensor('float32', prep.tensor, [1, 3, prep.size, prep.size]);
        // DepthAnything uses 'pixel_values' as input name; fall back to first
        // input if the model was exported under a different alias.
        var feeds = {};
        var name = session.inputNames && session.inputNames[0] || 'pixel_values';
        feeds[name] = input;
        return session.run(feeds).then(function (outputs) {
            var outName = Object.keys(outputs)[0];
            var out = outputs[outName];
            var arr = out.data;
            var dims = out.dims;
            var h = dims[dims.length - 2];
            var w = dims[dims.length - 1];
            // Normalise to [0,1] — closer = 1 (DepthAnything emits relative depth).
            var min = Infinity, max = -Infinity;
            for (var i = 0; i < arr.length; i++) {
                if (arr[i] < min) min = arr[i];
                if (arr[i] > max) max = arr[i];
            }
            var range = (max - min) || 1;
            var depth = new Float32Array(arr.length);
            for (var j = 0; j < arr.length; j++) depth[j] = (arr[j] - min) / range;
            return { depth: depth, w: w, h: h, raw: { min: min, max: max } };
        });
    }

    /**
     * heightmapToReliefMesh(hm, w, h, targetW, targetH, baseMm, reliefMm)
     * — grid triangulation that mirrors the flat-lithophane logic but for a
     *   0..1 heightmap. Returns { positions, normals }. Watertight: top +
     *   bottom + 4 side strips.
     */
    /**
     * shapedReliefMesh(hm, mask, w, h, opts) — рельеф ЛЮБОЙ формы, а не только
     * прямоугольной плиткой.
     *
     * Зачем: замер 04.09 на эталоне (рендер 3D-человека + его истинная глубина)
     * показал, что монокулярная глубина на самом частом сюжете — человек анфас —
     * почти не несёт сигнала (корреляция с истиной 0.10 против 0.85-0.95 у
     * головы крупным планом), и ни разрешение инференса, ни смена модели этого
     * не меняют. Зато ФОРМА вывода меняет всё: та же карта высот, вырезанная по
     * контуру предмета и поставленная вертикально, читается как статуэтка, а не
     * как плитка с буграми. Треугольников при этом втрое меньше — меш строится
     * только по предмету.
     *
     * opts:
     *   mode      'plaque' (плоская спина) | 'puffed' (рельеф зеркалится назад)
     *   backScale насколько мельче спина у 'puffed' (1 = симметрично)
     *   taper     сколько ячеек занимает скос у края выреза (0 = отвесная стенка)
     *   standMm   поставить вертикально и подложить подставку такой толщины
     *
     * ⚠️ Стены строятся ПО ЯЧЕЙКАМ, а не по флагам вершин: вершинный вариант
     * даёт «занавески» внутри фигуры и 1277 открытых рёбер вместо нуля.
     * ⚠️ Маска чистится ДО построения (дырки в одну ячейку, одиночки, касания по
     * диагонали) — без этого остаются открытые рёбра, а водонепроницаемость это
     * единственное, что тул обещает про вывод.
     */
    /**
     * otsuMask(depth, w, h) — ближняя половина кадра по порогу Оцу.
     * Бесплатна: карта глубины уже посчитана.
     */
    function otsuMask(depth, w, h) {
        var B = 64, hist = new Array(B), i;
        for (i = 0; i < B; i++) hist[i] = 0;
        for (i = 0; i < depth.length; i++) {
            var b = Math.floor(depth[i] * B);
            hist[b < 0 ? 0 : (b >= B ? B - 1 : b)]++;
        }
        var total = depth.length, sum = 0;
        for (i = 0; i < B; i++) sum += i * hist[i];
        var sumB = 0, wB = 0, best = -1, thr = 0;
        for (i = 0; i < B; i++) {
            wB += hist[i]; if (!wB) continue;
            var wF = total - wB; if (!wF) break;
            sumB += i * hist[i];
            var mB = sumB / wB, mF = (sum - sumB) / wF;
            var v = wB * wF * (mB - mF) * (mB - mF);
            if (v > best) { best = v; thr = i; }
        }
        var cut = (thr + 1) / B;
        var m = new Uint8Array(w * h);
        for (i = 0; i < depth.length; i++) m[i] = depth[i] >= cut ? 1 : 0;
        return m;
    }

    /** Самый большой связный кусок маски: обрывки фона предметом не являются. */
    function largestBlob(mask, w, h) {
        var lab = new Int32Array(w * h), i;
        for (i = 0; i < lab.length; i++) lab[i] = -1;
        var best = -1, bestN = 0, id = 0, stack = [];
        for (var s0 = 0; s0 < mask.length; s0++) {
            if (!mask[s0] || lab[s0] >= 0) continue;
            var n = 0; stack.push(s0); lab[s0] = id;
            while (stack.length) {
                var q = stack.pop(); n++;
                var qx = q % w, qy = (q - qx) / w;
                if (qx > 0     && mask[q-1] && lab[q-1] < 0) { lab[q-1] = id; stack.push(q-1); }
                if (qx < w - 1 && mask[q+1] && lab[q+1] < 0) { lab[q+1] = id; stack.push(q+1); }
                if (qy > 0     && mask[q-w] && lab[q-w] < 0) { lab[q-w] = id; stack.push(q-w); }
                if (qy < h - 1 && mask[q+w] && lab[q+w] < 0) { lab[q+w] = id; stack.push(q+w); }
            }
            if (n > bestN) { bestN = n; best = id; }
            id++;
        }
        var out = new Uint8Array(w * h);
        for (i = 0; i < out.length; i++) out[i] = lab[i] === best ? 1 : 0;
        return { mask: out, count: bestN };
    }

    /**
     * pickSubjectMask(otsu, rvm, w, h) — какую маску брать под вырез.
     *
     * Замер 04.09 на истинных силуэтах (12 сцен, IoU / совпадение КОНТУРА):
     *   MediaPipe selfie      0.716 / 0.197   ← стояло в туле
     *   порог Оцу по глубине  0.429 / 0.534
     *   RVM MobileNetV3       0.925 / 0.881
     *   выбор из двух ниже    0.949 / 0.917
     *
     * Движки слепнут в ПРОТИВОПОЛОЖНЫХ местах: RVM обучен на людях в полный
     * рост и на крупном плане лица теряет предмет (0.688 против 0.925 у Оцу),
     * а Оцу даёт НОЛЬ на дальних планах — порог там режет по полу.
     *
     * ⚠️ Условие описывает сам ОТКАЗ, а не подогнанное число: «глубина говорит,
     * что предмет заполняет кадр, а RVM вернул заметно меньше — значит потерял
     * его». Порог, подобранный по доле кадра, сработал бы ровно на двух сценах
     * из двенадцати, то есть был бы подогнан под выборку.
     */
    function pickSubjectMask(otsu, rvm, w, h) {
        var area = w * h;
        var o = largestBlob(otsu, w, h), r = largestBlob(rvm, w, h);
        var oFrac = o.count / area, rFrac = r.count / area;
        if (oFrac >= 0.5 && rFrac < oFrac * 0.7)
            return { mask: o.mask, count: o.count, source: 'otsu', reason: 'rvm_lost_close_subject' };
        return { mask: r.mask, count: r.count, source: 'rvm', reason: '' };
    }

    function shapedReliefMesh(hm, mask, w, h, opts) {
        opts = opts || {};
        var targetW = opts.targetW, targetH = opts.targetH;
        var base = opts.baseMm, relief = opts.reliefMm;
        var sx = targetW / (w - 1), sy = targetH / (h - 1);
        var cw = w - 1, ch = h - 1;
        var puffed = opts.mode === 'puffed';
        var backScale = opts.backScale === undefined ? 1 : opts.backScale;
        var pos = [];

        var cell = new Uint8Array(cw * ch), i, j;
        for (j = 0; j < ch; j++) for (i = 0; i < cw; i++)
            cell[j*cw+i] = (!mask || (mask[j*w+i] && mask[j*w+i+1] && mask[(j+1)*w+i] && mask[(j+1)*w+i+1])) ? 1 : 0;
        if (mask) cleanCellMask(cell, cw, ch);

        // Скос у края выреза: высота гаснет к контуру, поэтому силуэт не
        // обрывается отвесной стенкой в полный рельеф.
        var edgeW = (mask && opts.taper > 0) ? taperWeights(cell, cw, ch, opts.taper) : null;

        function hAt(i2, j2) {
            var v = hm[j2*w+i2];
            if (edgeW) {
                var ci = i2 >= cw ? cw - 1 : i2;
                var cj = j2 >= ch ? ch - 1 : j2;
                v *= edgeW[cj*cw+ci];
            }
            return v;
        }
        function top(i2, j2) { return [-targetW/2 + i2*sx, base + hAt(i2,j2)*relief, -targetH/2 + j2*sy]; }
        function low(i2, j2) {
            return puffed
                ? [-targetW/2 + i2*sx, -(base + hAt(i2,j2)*relief*backScale), -targetH/2 + j2*sy]
                : [-targetW/2 + i2*sx, 0, -targetH/2 + j2*sy];
        }
        function tri(a, b, c) { pos.push(a[0],a[1],a[2], b[0],b[1],b[2], c[0],c[1],c[2]); }
        function has(i2, j2) { return i2 >= 0 && j2 >= 0 && i2 < cw && j2 < ch && !!cell[j2*cw+i2]; }

        for (j = 0; j < ch; j++) for (i = 0; i < cw; i++) {
            if (!cell[j*cw+i]) continue;
            var a = top(i,j), b = top(i+1,j), c = top(i+1,j+1), d = top(i,j+1);
            tri(a,b,c); tri(a,c,d);
            var A = low(i,j), B = low(i+1,j), C = low(i+1,j+1), D = low(i,j+1);
            tri(A,C,B); tri(A,D,C);
            if (!has(i-1,j)) { tri(d,a,A); tri(d,A,D); }
            if (!has(i+1,j)) { tri(b,c,C); tri(b,C,B); }
            if (!has(i,j-1)) { tri(a,b,B); tri(a,B,A); }
            if (!has(i,j+1)) { tri(c,d,D); tri(c,D,C); }
        }
        var positions = new Float32Array(pos);
        if (opts.standMm) positions = standReliefUp(positions, opts.standMm);
        return { positions: positions, normals: computeNormals(positions) };
    }

    /**
     * Заливка дырок в одну ячейку, снос одиночек, разрыв касаний по диагонали,
     * плюс сглаживание контура по большинству соседей.
     * Сглаживание — не косметика: граница маски идёт по сетке ступеньками, и в
     * вырезанной форме каждая ступенька становится видимой стенкой в полный
     * рельеф. Большинство из 8 соседей срезает их, не трогая саму фигуру.
     */
    function cleanCellMask(cell, cw, ch) {
        function at(a, i, j) { return (i < 0 || j < 0 || i >= cw || j >= ch) ? 0 : a[j*cw+i]; }
        for (var sp = 0; sp < 2; sp++) {
            var src8 = cell.slice();
            for (var sj = 0; sj < ch; sj++) for (var si = 0; si < cw; si++) {
                var n8 = at(src8,si-1,sj-1) + at(src8,si,sj-1) + at(src8,si+1,sj-1)
                       + at(src8,si-1,sj)   +                    at(src8,si+1,sj)
                       + at(src8,si-1,sj+1) + at(src8,si,sj+1) + at(src8,si+1,sj+1);
                if (n8 >= 6) cell[sj*cw+si] = 1;
                else if (n8 <= 2) cell[sj*cw+si] = 0;
            }
        }
        for (var pass = 0; pass < 2; pass++) {
            var src = cell.slice();
            for (var j = 0; j < ch; j++) for (var i = 0; i < cw; i++) {
                var n = at(src,i-1,j) + at(src,i+1,j) + at(src,i,j-1) + at(src,i,j+1);
                if (!src[j*cw+i] && n >= 3) cell[j*cw+i] = 1;
                else if (src[j*cw+i] && n <= 1) cell[j*cw+i] = 0;
            }
        }
        for (var j2 = 0; j2 < ch - 1; j2++) for (var i2 = 0; i2 < cw - 1; i2++) {
            var a = cell[j2*cw+i2], b = cell[j2*cw+i2+1];
            var c = cell[(j2+1)*cw+i2], d = cell[(j2+1)*cw+i2+1];
            if (a && d && !b && !c) cell[(j2+1)*cw+i2] = 1;
            if (b && c && !a && !d) cell[j2*cw+i2] = 1;
        }
    }

    /** Вес высоты у края выреза: 0 на контуре, 1 на расстоянии n ячеек внутрь. */
    function taperWeights(cell, cw, ch, n) {
        var dist = new Float32Array(cw*ch), i, j, pass;
        for (i = 0; i < dist.length; i++) dist[i] = cell[i] ? n + 1 : 0;
        function at(x, y) { return (x < 0 || y < 0 || x >= cw || y >= ch) ? 0 : dist[y*cw+x]; }
        for (pass = 0; pass < n; pass++)
            for (j = 0; j < ch; j++) for (i = 0; i < cw; i++) {
                if (!cell[j*cw+i]) continue;
                dist[j*cw+i] = Math.min(dist[j*cw+i], Math.min(at(i-1,j), at(i+1,j), at(i,j-1), at(i,j+1)) + 1);
            }
        var wgt = new Float32Array(cw*ch);
        for (i = 0; i < wgt.length; i++) wgt[i] = Math.min(1, dist[i] / n);
        return wgt;
    }

    /**
     * standReliefUp(positions, plinthMm) — поставить рельеф вертикально и
     * подложить подставку. Плоскость рельефа (x,z) становится (x,y).
     * ⚠️ Печатать такую модель всё равно правильнее ЛЁЖА (спиной на стол):
     * подставка нужна, чтобы результат стоял на полке, а не чтобы менять
     * ориентацию печати.
     */
    function standReliefUp(positions, plinthMm) {
        var p = new Float32Array(positions.length), i;
        var minX = Infinity, maxX = -Infinity, minY = Infinity, minZ = Infinity, maxZ = -Infinity;
        for (i = 0; i < positions.length; i += 3) {
            var x = positions[i], y = positions[i+1], z = positions[i+2];
            p[i] = x; p[i+1] = -z; p[i+2] = y;
            if (x < minX) minX = x; if (x > maxX) maxX = x;
            if (p[i+1] < minY) minY = p[i+1];
            if (y < minZ) minZ = y; if (y > maxZ) maxZ = y;
        }
        for (i = 1; i < p.length; i += 3) p[i] -= minY;
        if (!plinthMm) return p;
        var pw = (maxX - minX) * 1.06;
        var pd = Math.max((maxZ - minZ) * 1.7, (maxX - minX) * 0.35);
        var cx = (minX + maxX) / 2, cz = (minZ + maxZ) / 2, t = plinthMm;
        var box = [];
        function V(dx, dy, dz) { return [cx + dx*pw/2, dy*t, cz + dz*pd/2]; }
        function quad(a, b, c, d) {
            box.push(a[0],a[1],a[2], b[0],b[1],b[2], c[0],c[1],c[2],
                     a[0],a[1],a[2], c[0],c[1],c[2], d[0],d[1],d[2]);
        }
        var v000=V(-1,0,-1), v100=V(1,0,-1), v110=V(1,0,1), v010=V(-1,0,1);
        var u000=V(-1,-1,-1), u100=V(1,-1,-1), u110=V(1,-1,1), u010=V(-1,-1,1);
        quad(v000,v100,v110,v010); quad(u010,u110,u100,u000);
        quad(u000,u100,v100,v000); quad(u110,u010,v010,v110);
        quad(u100,u110,v110,v100); quad(u010,u000,v000,v010);
        var out = new Float32Array(p.length + box.length);
        out.set(p, 0); out.set(box, p.length);
        // Подставка строится вниз от фигуры, поэтому после слияния низ модели
        // уходит под стол. Слайсер такую модель либо опустит сам, либо срежет —
        // прижимаем к нулю здесь, а не надеемся на чужое поведение.
        var lowest = Infinity;
        for (i = 1; i < out.length; i += 3) if (out[i] < lowest) lowest = out[i];
        if (lowest !== 0) for (i = 1; i < out.length; i += 3) out[i] -= lowest;
        return out;
    }

    function heightmapToReliefMesh(hm, w, h, targetW, targetH, baseMm, reliefMm) {
        var positions = [], normals = [];
        var stepX = targetW / (w - 1);
        var stepY = targetH / (h - 1);
        function vtx(i, j) {
            return [
                -targetW / 2 + i * stepX,
                baseMm + hm[j * w + i] * reliefMm,
                -targetH / 2 + j * stepY,
            ];
        }
        function bv(i, j) { return [-targetW / 2 + i * stepX, 0, -targetH / 2 + j * stepY]; }
        function tn(a, b, c) {
            var ax = b[0]-a[0], ay = b[1]-a[1], az = b[2]-a[2];
            var bx = c[0]-a[0], by = c[1]-a[1], bz = c[2]-a[2];
            var nx = ay*bz - az*by, ny = az*bx - ax*bz, nz = ax*by - ay*bx;
            var L = Math.sqrt(nx*nx + ny*ny + nz*nz) || 1;
            return [nx/L, ny/L, nz/L];
        }
        // Top
        for (var j = 0; j < h - 1; j++) {
            for (var i = 0; i < w - 1; i++) {
                var a = vtx(i, j), b = vtx(i+1, j), c = vtx(i+1, j+1), d = vtx(i, j+1);
                var n1 = tn(a, b, c), n2 = tn(a, c, d);
                positions.push(a[0],a[1],a[2], b[0],b[1],b[2], c[0],c[1],c[2]);
                for (var k = 0; k < 3; k++) normals.push(n1[0],n1[1],n1[2]);
                positions.push(a[0],a[1],a[2], c[0],c[1],c[2], d[0],d[1],d[2]);
                for (var k2 = 0; k2 < 3; k2++) normals.push(n2[0],n2[1],n2[2]);
            }
        }
        // Bottom
        for (var j2 = 0; j2 < h - 1; j2++) {
            for (var i2 = 0; i2 < w - 1; i2++) {
                var a2 = bv(i2, j2), b2 = bv(i2+1, j2), c2 = bv(i2+1, j2+1), d2 = bv(i2, j2+1);
                positions.push(a2[0],a2[1],a2[2], c2[0],c2[1],c2[2], b2[0],b2[1],b2[2]);
                positions.push(a2[0],a2[1],a2[2], d2[0],d2[1],d2[2], c2[0],c2[1],c2[2]);
                for (var k3 = 0; k3 < 6; k3++) normals.push(0, -1, 0);
            }
        }
        // Side walls
        function sideQuad(a, b, aB, bB, nx, ny, nz) {
            positions.push(a[0],a[1],a[2], b[0],b[1],b[2], bB[0],bB[1],bB[2]);
            positions.push(a[0],a[1],a[2], bB[0],bB[1],bB[2], aB[0],aB[1],aB[2]);
            for (var k = 0; k < 6; k++) normals.push(nx, ny, nz);
        }
        for (var j3 = 0; j3 < h - 1; j3++) {
            var L1 = vtx(0, j3), L2 = vtx(0, j3+1);
            var LB1 = bv(0, j3),  LB2 = bv(0, j3+1);
            sideQuad(L2, L1, LB2, LB1, -1, 0, 0);
            var R1 = vtx(w-1, j3), R2 = vtx(w-1, j3+1);
            var RB1 = bv(w-1, j3),  RB2 = bv(w-1, j3+1);
            sideQuad(R1, R2, RB1, RB2, 1, 0, 0);
        }
        for (var i3 = 0; i3 < w - 1; i3++) {
            var F1 = vtx(i3, 0), F2 = vtx(i3+1, 0);
            var FB1 = bv(i3, 0),  FB2 = bv(i3+1, 0);
            sideQuad(F1, F2, FB1, FB2, 0, 0, -1);
            var B1 = vtx(i3, h-1), B2 = vtx(i3+1, h-1);
            var BB1 = bv(i3, h-1),  BB2 = bv(i3+1, h-1);
            sideQuad(B2, B1, BB2, BB1, 0, 0, 1);
        }
        return { positions: new Float32Array(positions), normals: new Float32Array(normals) };
    }

    global.Timbrica3D = {
        FILAMENT_PRESETS: FILAMENT_PRESETS,
        densityFor: densityFor,
        filamentVolumeCm3: filamentVolumeCm3,
        filamentWeightG: filamentWeightG,
        filamentLengthM: filamentLengthM,
        parseSTL: parseSTL,
        writeBinarySTL: writeBinarySTL,
        computeNormals: computeNormals,
        downloadBlob: downloadBlob,
        formatSize: formatSize,
        formatNumber: formatNumber,
        makeViewer: makeViewer,
        meshFromSTL: meshFromSTL,
        ae: ae,
        aeCritical: aeCritical,
        handleError: handleError,
        init: init,
        throttle: throttle,
        withStalledWatchdog: withStalledWatchdog,
        loadManifold: loadManifold,
        loadFont: loadFont,
        manifoldMeshToGeometry: manifoldMeshToGeometry,
        geometryToSTL: geometryToSTL,
        extrudePolygon: extrudePolygon,
        checkWatertight: checkWatertight,
        textToManifold: textToManifold,
        inspectSolid: inspectSolid,
        assertPrintable: assertPrintable,
        loadONNX: loadONNX,
        probeWebGPU: probeWebGPU,
        loadDepthSession: loadDepthSession,
        imageToDepthTensor: imageToDepthTensor,
        isolateSubjectDepth: isolateSubjectDepth,
        runDepthInference: runDepthInference,
        heightmapToReliefMesh: heightmapToReliefMesh,
        shapedReliefMesh: shapedReliefMesh,
        otsuMask: otsuMask,
        largestBlob: largestBlob,
        pickSubjectMask: pickSubjectMask,
        standReliefUp: standReliefUp,
    };
})(typeof window !== 'undefined' ? window : globalThis);
